/*
  BB Live Blocks — keeps server-rendered BB product blocks in sync with the selected variant.

  How it works: when a shopper picks a variant, Horizon fetches the product section for that
  variant and fires the `product:select` standard event with a promise that resolves to the
  new HTML. Horizon's own <product-inventory> uses exactly this to refresh itself. We do the
  same for any <bb-live data-bb-key="…">: find the element with the same key in the new HTML
  and swap its contents. All logic (stock counts, shipping thresholds, trust copy) therefore
  lives in Liquid — nothing is duplicated in JS, and it works inside Quick View dialogs too.

  Also renders delivery promises (.bb-eta):
    · UAE (data-cutoff="13"): order before 13:00 UAE time → delivered the next day, with a live
      countdown; otherwise the day after that. The UAE courier delivers 7 days a week
      (merchant rule, 2026-11-01), so every day counts.
    · Other regions: a min–max business-day date range (Monday–Friday).
  Times are UAE time (Asia/Dubai, UTC+4, no DST).

  Size: ~2 KB, no dependencies. Loaded once as a module from snippets/bb-head.liquid.
*/
import { StandardEvents } from '@shopify/events';

const UAE_OFFSET_MS = 4 * 60 * 60 * 1000;

/** "Now" expressed in UAE wall-clock time, read back through the UTC getters. */
const uaeNow = () => new Date(Date.now() + UAE_OFFSET_MS);
const isBusinessDay = (d) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6;

function addBusinessDays(from, n) {
  const d = new Date(from);
  while (n > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (isBusinessDay(d)) n--;
  }
  return d;
}

function addDays(from, n) {
  const d = new Date(from);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

function formatDate(d, ar) {
  return d.toLocaleDateString(ar ? 'ar-AE-u-nu-latn' : 'en-AE', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function renderEta(el) {
  const ar = el.dataset.lang === 'ar';
  const now = uaeNow();
  const cutoff = Number(el.dataset.cutoff);

  if (cutoff) {
    const beforeCutoff = now.getUTCHours() < cutoff;
    const arrival = addDays(now, beforeCutoff ? 1 : 2);
    if (beforeCutoff) {
      const minutesLeft = cutoff * 60 - (now.getUTCHours() * 60 + now.getUTCMinutes());
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      const left = ar ? `${h ? `${h}س ` : ''}${m}د` : `${h ? `${h}h ` : ''}${m}m`;
      el.textContent = ar
        ? `اطلبي خلال ${left} ليصلك ${formatDate(arrival, true)}`
        : `Order within ${left} to get it ${formatDate(arrival, false)}`;
    } else {
      el.textContent = ar ? `يصلك ${formatDate(arrival, true)}` : `Arrives ${formatDate(arrival, false)}`;
    }
    return;
  }

  const min = addBusinessDays(now, Number(el.dataset.min));
  const max = addBusinessDays(now, Number(el.dataset.max));
  el.textContent = `${ar ? 'يصلك بين' : 'Arrives'} ${formatDate(min, ar)} ${ar ? 'و' : '–'} ${formatDate(max, ar)}`;
}

const renderAllEta = (root) => root.querySelectorAll('.bb-eta').forEach(renderEta);

// Keep countdowns honest while the page stays open.
setInterval(() => renderAllEta(document), 60 * 1000);

class BBLive extends HTMLElement {
  connectedCallback() {
    renderAllEta(this);
    this.scope = this.closest('dialog, .shopify-section');
    this.scope?.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const next = html.querySelector(`bb-live[data-bb-key="${this.dataset.bbKey}"]`);
        if (!next) return;
        this.innerHTML = next.innerHTML;
        renderAllEta(this);
        this.dispatchEvent(new CustomEvent('bb:live-updated', { bubbles: true }));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bb-live]', error);
      });
  };
}

if (!customElements.get('bb-live')) customElements.define('bb-live', BBLive);
